'use client';

import { FormEvent, useRef, useState } from 'react';

import { getPlaneKeyframes } from '@/lib/getPlaneKeyframes';
import { getTrailsKeyframes } from '@/lib/getTrailsKeyframes';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid';

import { gsap } from 'gsap';

export default function NewsletterForm() {
  const [input, setInput] = useState('');
  const [successMessage, setSuccessMessage] =
    useState<MembersSuccessResponse>();
  const [errorMessage, setErrorMessage] = useState('');
  const [active, setActive] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { to, fromTo, set } = gsap;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const email = input;
    const button = buttonRef.current;

    if (!email || !button) return;

    // Track form submission event in Google Analytics
    window.gtag('event', 'form_submission', {
      event_category: 'Newsletter',
      event_label: 'Subscription Form Submitted'
    });

    if (!active) {
      setActive(true);

      to(button, {
        keyframes: getPlaneKeyframes(set, fromTo, button, setActive, setInput)
      });

      to(button, { keyframes: getTrailsKeyframes(button) });
    }

    try {
      const res = await fetch('/api/addSubscription', {
        body: JSON.stringify({ email }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST'
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error);
      setSuccessMessage({ email_address: email } as MembersSuccessResponse);
      setErrorMessage('');
    } catch (error) {
      setErrorMessage(
        error instanceof Error && error.message
          ? error.message
          : 'Something went wrong. Please try again.'
      );
      setSuccessMessage(undefined);
    }
  };

  const dismissMessages = () => {
    setSuccessMessage(undefined);
    setErrorMessage('');
  };

  return (
    <div className='relative z-10 mx-auto flex w-full max-w-xl flex-col space-y-6'>
      <form
        onSubmit={handleSubmit}
        className='newsletter-form animate-fade-in-3'>
        <div
          className='group relative flex items-center gap-x-3 overflow-hidden rounded-full py-1.5 pr-1.5 pl-5 shadow-xs transition-all duration-300 focus-within:ring-2'
          style={{
            backgroundColor: 'var(--card)',
            border:
              '1px solid color-mix(in srgb, var(--primary) 50%, transparent)',
            borderColor: 'color-mix(in srgb, var(--primary) 50%, transparent)'
          }}>
          <EnvelopeIcon
            className='hidden size-5 shrink-0 opacity-40 transition-opacity duration-300 group-focus-within:opacity-100 group-hover:opacity-100 sm:inline'
            style={{ color: 'var(--foreground)' }}
          />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder='Email address'
            required
            type='email'
            className='min-w-0 flex-1 !border-0 !bg-transparent !p-0 text-sm !shadow-none !ring-0 !outline-none focus:!border-0 focus:!shadow-none focus:!ring-0 focus:!outline-none sm:text-base [&:-webkit-autofill]:[-webkit-text-fill-color:var(--foreground)] [&:-webkit-autofill]:[transition:background-color_9999999s_ease-in-out_0s]'
            style={{ color: 'var(--foreground)' }}
          />
          <button
            ref={buttonRef}
            className={`${
              active && 'active'
            } relative z-10 rounded-full !px-6 !py-2.5 text-sm whitespace-nowrap transition-colors disabled:cursor-not-allowed disabled:opacity-60 md:text-base`}
            disabled={!input}
            type='submit'>
            <span className='default'>Subscribe</span>
            <span className='success'>
              <svg viewBox='0 0 16 16'>
                <polyline points='3.75 9 7 12 13 5'></polyline>
              </svg>
              Done
            </span>
            <svg
              className='trails'
              viewBox='0 0 33 64'>
              <path d='M26,4 C28,13.3333333 29,22.6666667 29,32 C29,41.3333333 28,50.6666667 26,60'></path>
              <path d='M6,4 C8,13.3333333 9,22.6666667 9,32 C9,41.3333333 8,50.6666667 6,60'></path>
            </svg>
            <div className='plane'>
              <div className='left'></div>
              <div className='right'></div>
            </div>
          </button>
        </div>
      </form>

      <div className='relative z-20 mt-4 w-full'>
        {(successMessage || errorMessage) && (
          <div className='animate-fade-bottom bg-card border-border/50 text-foreground flex w-full max-w-sm items-start space-x-3 rounded-2xl border px-5 py-4 shadow-xl dark:bg-black/40'>
            <div className='border-border/50 bg-muted/50 mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full border'>
              <CheckIcon
                className={`size-4 ${successMessage ? 'text-primary' : 'text-destructive'}`}
              />
            </div>
            <div className='text-muted-foreground flex-1 text-sm leading-relaxed'>
              {successMessage ? (
                <p>
                  Almost done. We sent a confirmation link to{' '}
                  <span className='text-foreground font-semibold'>
                    {successMessage.email_address}
                  </span>
                  . Click it to start receiving the weekly digest.
                </p>
              ) : (
                <p>{errorMessage}</p>
              )}
            </div>
            <XMarkIcon
              className='text-muted-foreground hover:text-foreground size-5 shrink-0 cursor-pointer transition-colors'
              onClick={dismissMessages}
            />
          </div>
        )}
      </div>
    </div>
  );
}
